import type {
  TPlayerRemoteCommand,
  TPlayerRemoteCommandResult
} from '@lucimark/shared/models/player-remote-control'
import {
  PLAYER_REMOTE_FRAME_JPEG_MAX_BASE64_CHARS,
  playerRemoteCommandResultSchema
} from '@lucimark/shared/models/player-remote-control'
import type { APIClientService } from '@/services/APIClientService'
import type { TRemoteFrameStreamer } from '@/helpers/playerRemote/remoteFrameStreamer'
import { createRemoteFrameStreamer } from '@/helpers/playerRemote/remoteFrameStreamer'
import {
  isAccessibilityServiceEnabled,
  remoteSwipe,
  remoteTakeScreenshot,
  remoteTap
} from '@/helpers/androidAccessibility'

/** In-memory remote-view session latch (ms epoch). */
let remoteSessionActiveUntilMs = 0

export function getPlayerRemoteSessionActiveUntilMs(): number {
  return remoteSessionActiveUntilMs
}

/** Test helper. */
export function resetPlayerRemoteSessionForTests(): void {
  remoteSessionActiveUntilMs = 0
}

function isCommandExpired(command: TPlayerRemoteCommand, nowMs: number): boolean {
  return nowMs > command.issuedAt + command.ttlSeconds * 1000
}

function isSessionActive(nowMs: number): boolean {
  return nowMs < remoteSessionActiveUntilMs
}

export function sanitizePlayerRemoteCommandResult(
  result: TPlayerRemoteCommandResult
): TPlayerRemoteCommandResult {
  if (result.ok && result.frameJpegBase64 != null
    && result.frameJpegBase64.length > PLAYER_REMOTE_FRAME_JPEG_MAX_BASE64_CHARS) {
    return {
      commandId: result.commandId,
      ok: false,
      error: 'frame_too_large'
    }
  }
  const body = playerRemoteCommandResultSchema.safeParse(result)
  if (!body.success) {
    return {
      commandId: result.commandId,
      ok: false,
      error: 'result_invalid'
    }
  }
  return body.data
}

async function postCommandResult(
  apiClient: APIClientService,
  result: TPlayerRemoteCommandResult
): Promise<void> {
  const payload = sanitizePlayerRemoteCommandResult(result)
  try {
    await apiClient.postPlayerRemoteCommandResult(payload)
  }
  catch (err) {
    console.warn(JSON.stringify({
      tag: 'player_remote_command_result_failed',
      commandId: payload.commandId,
      ok: payload.ok,
      message: err instanceof Error ? err.message : String(err)
    }))
  }
}

/** Pure-ish executor (async for native invokes). */
export async function executePlayerRemoteCommand(
  command: TPlayerRemoteCommand
): Promise<TPlayerRemoteCommandResult> {
  const nowMs = Date.now()
  if (isCommandExpired(command, nowMs)) {
    return { commandId: command.commandId, ok: false, error: 'command_expired' }
  }

  switch (command.type) {
    case 'startRemoteView': {
      const accessibilityEnabled = await isAccessibilityServiceEnabled()
      if (!accessibilityEnabled) {
        return {
          commandId: command.commandId,
          ok: false,
          error: 'accessibility_disabled',
          accessibilityEnabled: false
        }
      }
      remoteSessionActiveUntilMs = nowMs + command.sessionTtlSeconds * 1000
      return {
        commandId: command.commandId,
        ok: true,
        sessionActiveUntil: remoteSessionActiveUntilMs,
        accessibilityEnabled: true,
        // Capability announcement: the app may now follow up with `startRemoteStream`.
        streamingSupported: true
      }
    }
    case 'startRemoteStream': {
      if (!isSessionActive(nowMs)) {
        return { commandId: command.commandId, ok: false, error: 'session_inactive' }
      }
      return {
        commandId: command.commandId,
        ok: true,
        sessionActiveUntil: remoteSessionActiveUntilMs,
        accessibilityEnabled: true,
        // Tells the operator's app it can stop polling: frames are pushed from here.
        streaming: true
      }
    }
    case 'stopRemoteView': {
      remoteSessionActiveUntilMs = 0
      return { commandId: command.commandId, ok: true, accessibilityEnabled: await isAccessibilityServiceEnabled() }
    }
    case 'captureRemoteFrame': {
      if (!isSessionActive(nowMs)) {
        return { commandId: command.commandId, ok: false, error: 'session_inactive' }
      }
      const frame = await remoteTakeScreenshot()
      if (frame == null) {
        return {
          commandId: command.commandId,
          ok: false,
          error: 'screenshot_unavailable',
          accessibilityEnabled: await isAccessibilityServiceEnabled()
        }
      }
      return {
        commandId: command.commandId,
        ok: true,
        frameJpegBase64: frame.jpegBase64,
        frameWidth: frame.width,
        frameHeight: frame.height,
        sessionActiveUntil: remoteSessionActiveUntilMs,
        accessibilityEnabled: true
      }
    }
    case 'remoteTap': {
      if (!isSessionActive(nowMs)) {
        return { commandId: command.commandId, ok: false, error: 'session_inactive' }
      }
      const ok = await remoteTap(command.x, command.y)
      return ok
        ? { commandId: command.commandId, ok: true, sessionActiveUntil: remoteSessionActiveUntilMs }
        : { commandId: command.commandId, ok: false, error: 'tap_failed' }
    }
    case 'remoteSwipe': {
      if (!isSessionActive(nowMs)) {
        return { commandId: command.commandId, ok: false, error: 'session_inactive' }
      }
      const ok = await remoteSwipe({
        x1: command.x1,
        y1: command.y1,
        x2: command.x2,
        y2: command.y2,
        durationMs: command.durationMs
      })
      return ok
        ? { commandId: command.commandId, ok: true, sessionActiveUntil: remoteSessionActiveUntilMs }
        : { commandId: command.commandId, ok: false, error: 'swipe_failed' }
    }
    default: {
      return {
        commandId: (command as { commandId: string }).commandId,
        ok: false,
        error: 'unknown_command'
      }
    }
  }
}

let frameStreamer: TRemoteFrameStreamer | null = null

function getFrameStreamer(apiClient: APIClientService): TRemoteFrameStreamer {
  frameStreamer ??= createRemoteFrameStreamer({
    capture: remoteTakeScreenshot,
    post: frame => apiClient.postPlayerRemoteFrame(frame),
    onStop: (reason) => {
      // The session latch keeps gestures working (tap/swipe) even after streaming ended on its own.
      console.info(JSON.stringify({ tag: 'player_remote_frame_stream_stopped', reason }))
    }
  })
  return frameStreamer
}

/** Test helper. */
export function resetPlayerRemoteFrameStreamerForTests(): void {
  frameStreamer?.stop()
  frameStreamer = null
}

/**
 * Fire-and-forget handler for operator remote WS commands. Must not block playback.
 */
export function handlePlayerRemoteCommand(
  apiClient: APIClientService,
  command: TPlayerRemoteCommand
): void {
  void (async () => {
    try {
      const result = await executePlayerRemoteCommand(command)
      await postCommandResult(apiClient, result)
      // Start after the result went out so the operator's app already knows to expect pushed frames.
      if (command.type === 'startRemoteStream' && result.ok && result.streaming === true) {
        getFrameStreamer(apiClient).start({ intervalMs: command.frameIntervalMs, untilMs: remoteSessionActiveUntilMs })
      }
      else if (command.type === 'stopRemoteView') {
        frameStreamer?.stop()
      }
    }
    catch (err) {
      await postCommandResult(apiClient, {
        commandId: command.commandId,
        ok: false,
        error: err instanceof Error ? err.message.slice(0, 512) : 'remote_command_failed'
      })
    }
  })()
}
